import { useEffect, useRef } from "react";
import type { PdfJsDocumentView } from "../presentation/cognitoryPdfJsGovernedPageLoader";
import { scrollGovernedPdfThumbnailRailToElement } from "../presentation/governedPdfCanvasRenderLifecycle";
import {
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_RADIUS,
} from "../theme/mainPageVisual";
import { GovernedCitationPdfThumbnail } from "./GovernedCitationPdfThumbnail";

const THUMBNAIL_SIDEBAR_WIDTH_PX = 112;

type GovernedCitationPdfThumbnailSidebarProps = {
  pdf: PdfJsDocumentView;
  numPages: number;
  citedPageNumber: number;
  currentPageNumber: number;
  expanded: boolean;
  drawerOpen: boolean;
  useDrawer: boolean;
  onToggleExpanded: () => void;
  onCloseDrawer: () => void;
  onSelectPage: (pageNumber: number) => void;
};

export function GovernedCitationPdfThumbnailSidebar(
  props: GovernedCitationPdfThumbnailSidebarProps,
): JSX.Element | null {
  const citedThumbnailRef = useRef<HTMLButtonElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledCitedThumbnailRef = useRef(false);

  const showRail = props.useDrawer ? props.drawerOpen : props.expanded;

  useEffect(() => {
    if (!showRail || hasScrolledCitedThumbnailRef.current) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const railRoot = railRef.current;
      const citedThumbnail = citedThumbnailRef.current;
      if (!railRoot || !citedThumbnail) {
        return;
      }
      scrollGovernedPdfThumbnailRailToElement({
        railRoot,
        targetElement: citedThumbnail,
      });
      hasScrolledCitedThumbnailRef.current = true;
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [props.citedPageNumber, showRail]);

  if (!props.useDrawer && !props.expanded) {
    return null;
  }

  const sidebarContent = (
    <aside
      data-testid="cognitory-governed-pdf-thumbnail-sidebar"
      data-drawer={props.useDrawer ? "true" : "false"}
      style={{
        width: props.useDrawer
          ? "min(82vw, 220px)"
          : THUMBNAIL_SIDEBAR_WIDTH_PX,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: props.useDrawer
          ? "none"
          : `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
        background: "rgba(255, 255, 255, 0.96)",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.5rem 0.55rem",
          borderBottom: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
        }}
      >
        <span
          style={{
            fontSize: "0.72rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: MAIN_PAGE_COLORS.whisper,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          Pages
        </span>
        <button
          type="button"
          data-testid="cognitory-governed-pdf-thumbnail-sidebar-close"
          onClick={() => {
            if (props.useDrawer) {
              props.onCloseDrawer();
              return;
            }
            props.onToggleExpanded();
          }}
          aria-label="Collapse page thumbnails"
          style={{
            border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
            background: MAIN_PAGE_COLORS.quietFill,
            borderRadius: MAIN_PAGE_RADIUS.alert,
            padding: "0.15rem 0.45rem",
            cursor: "pointer",
            color: MAIN_PAGE_COLORS.quietInk,
            fontFamily: MAIN_PAGE_FONT_STACK,
            fontSize: "0.78rem",
          }}
        >
          {props.useDrawer ? "Close" : "Hide"}
        </button>
      </div>

      <div
        ref={railRef}
        data-testid="cognitory-governed-pdf-thumbnail-rail"
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          padding: "0.55rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.55rem",
          flex: 1,
          minHeight: 0,
        }}
      >
        {Array.from({ length: props.numPages }, (_, index) => {
          const pageNumber = index + 1;
          const isCitedPage = pageNumber === props.citedPageNumber;
          const isCurrentPage = pageNumber === props.currentPageNumber;
          return (
            <button
              key={`thumbnail-nav-${pageNumber}`}
              type="button"
              ref={isCitedPage ? citedThumbnailRef : undefined}
              data-testid={`cognitory-governed-pdf-thumbnail-${pageNumber}`}
              data-cited-page={isCitedPage ? "true" : "false"}
              data-current-page={isCurrentPage ? "true" : "false"}
              aria-label={
                isCitedPage
                  ? `Go to cited page ${pageNumber}`
                  : `Go to page ${pageNumber}`
              }
              onClick={() => props.onSelectPage(pageNumber)}
              style={{
                border: isCurrentPage
                  ? `2px solid ${MAIN_PAGE_COLORS.link}`
                  : `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
                borderRadius: MAIN_PAGE_RADIUS.alert,
                background: isCurrentPage
                  ? "rgba(37, 99, 235, 0.06)"
                  : "rgba(255, 255, 255, 0.9)",
                padding: "0.35rem",
                cursor: "pointer",
                position: "relative",
                boxShadow: isCurrentPage
                  ? "0 0 0 1px rgba(37, 99, 235, 0.15)"
                  : "none",
              }}
            >
              {isCitedPage ? (
                <span
                  data-testid={`cognitory-governed-pdf-thumbnail-cited-marker-${pageNumber}`}
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "rgba(255, 235, 59, 0.95)",
                    border: `1px solid ${MAIN_PAGE_COLORS.warnBorder}`,
                    boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.8)",
                  }}
                />
              ) : null}
              <GovernedCitationPdfThumbnail
                pdf={props.pdf}
                pageNumber={pageNumber}
              />
              <span
                style={{
                  display: "block",
                  marginTop: "0.3rem",
                  fontSize: "0.72rem",
                  color: isCurrentPage
                    ? MAIN_PAGE_COLORS.link
                    : MAIN_PAGE_COLORS.soft,
                  fontFamily: MAIN_PAGE_FONT_STACK,
                  textAlign: "center",
                }}
              >
                {pageNumber}
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );

  if (props.useDrawer) {
    if (!props.drawerOpen) {
      return null;
    }
    return (
      <>
        <button
          type="button"
          data-testid="cognitory-governed-pdf-thumbnail-drawer-backdrop"
          aria-label="Close page thumbnails"
          onClick={props.onCloseDrawer}
          style={{
            position: "fixed",
            inset: 0,
            border: "none",
            background: "rgba(15, 23, 42, 0.35)",
            zIndex: 30,
            cursor: "pointer",
          }}
        />
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: 31,
            boxShadow: "4px 0 24px rgba(15, 23, 42, 0.18)",
          }}
        >
          {sidebarContent}
        </div>
      </>
    );
  }

  return sidebarContent;
}

export const GOVERNED_PDF_THUMBNAIL_SIDEBAR_WIDTH_PX =
  THUMBNAIL_SIDEBAR_WIDTH_PX;
